// @flow
import React, { useEffect, useRef, useState } from 'react';
import injectSheet from 'react-jss';
import { gql, useQuery } from '@apollo/client';
import Templates from 'shared/templates';
import Button from '~/app/ui/Button';
import TextInput from '~/app/components/TextInput';
import { S, DARK } from '~/app/ui/tokens';
import _ from 'shared/lib/gettext';
import { getFfaiPathError } from 'shared/lib/ffaiSite';
import ChainSelector from './ChainSelector';
import getSiteCategories, { findCategoryByPath } from './siteCategories';

// Helper function to transform path display from cxc10 to aixc
const transformPathDisplay = (pathStr) => {
  if (!pathStr) return pathStr;
  return pathStr.replace(/\/cxc10/g, '/aixc');
};

type Props = {
  classes: Object,
  disabled: boolean,
  copyLoading?: boolean,
  loadingLabel?: string,
  templateId?: string,
  templateName?: string,
  editorType?: 'genesis' | 'studio',
  title: string,
  category: string,
  path: string,
  slug: string,
  copyError: string,
  /** Category to choose once the tree loads, from the create link's ?category= (a path such as /us/events). */
  initialCategoryPath?: string,
  onCreate: Function
};

// Marks "no category picked yet", which shows as a hint rather than the permission error.
const PICK_CATEGORY = 'PICK_CATEGORY';

const PageSettings = (props: Props) => {
  const {
    classes: c,
    disabled,
    copyLoading = false,
    loadingLabel = 'Loading...',
    templateId,
    templateName,
    editorType = 'genesis',
    copyError
  } = props;

  // Helper function to validate AIXC path (internally stored as /cxc10)
  const validateAIXCPath = (pathValue, slugValue) => {
    if (!pathValue.startsWith('/cxc10')) {
      return ''; // Not an AIXC path, no error
    }
    
    // Construct the full path that will be created
    const fullPath = slugValue ? `${pathValue}/${slugValue}` : pathValue;
    
    // Check if the path contains /cxc10/en/ or /cxc10/cn/
    if (fullPath.match(/\/cxc10\/(en|cn)(\/|$)/)) {
      return ''; // Valid AIXC path
    }
    
    return 'AIXC pages must include /en/ or /cn/. (e.g., AIXCRYPTO.AI > en or AIXCRYPTO.AI > cn)';
  };

  // ffai.com pages live under /ffai/us/ or /ffai/cn/
  const validateSitePath = (pathValue, slugValue) => (
    validateAIXCPath(pathValue, slugValue) || getFfaiPathError(pathValue, slugValue)
  );

  const [state, setState] = useState({
    title: props.title || '',
    path: props.path || '',
    category: props.category || '',
    slug: props.slug || '',
    // Until a category is picked: a prompt, not an error (creating stays blocked).
    categoryError: PICK_CATEGORY,
    slugError: '',
    aixcError: validateSitePath(props.path || '', props.slug || '')
  });

  const {
    title,
    path,
    category,
    slug,
    categoryError,
    slugError,
    aixcError
  } = state;

  // getData
  const { data, loading: isFetching } = useQuery(QUERY, { fetchPolicy: 'cache-and-network' });
  const sitemap = data?.sitemap;

  const handlePathChange = (newPath, categoryId = category, canUserEdit) => {
    let categoryError = '';

    if (!canUserEdit) {
      categoryError = _('ERROR_CREATE_PAGE_CATEGORY');
    }

    const aixcError = validateSitePath(newPath, slug);

    setState(x => ({
      ...x,
      path: newPath,
      category: categoryId,
      categoryError,
      aixcError
    }));
  }

  const handleSlugChange = (newVal) => {
    const aixcError = validateSitePath(path, newVal);
    
    if (newVal.search(/^[a-zA-Z0-9_-]*$/) > -1) {
      setState(x => ({
        ...x,
        slugError: '',
        slug: newVal,
        aixcError
      }));
    } else {
      setState(x => ({
        ...x,
        slugError: _('ERROR_CREATE_PAGE_PATH_SLUG'),
        aixcError
      }));
    }
  }

  const handleTitleChange = (newVal) => {
    setState(x => ({
      ...x,
      title: newVal
    }));
  }

  // Generate URL-friendly slug from title
  const handleGenerateSlug = () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle || categoryError || !category) {
      return;
    }

    // Convert title to URL-friendly slug:
    // 1. Convert to lowercase
    // 2. Replace spaces and underscores with hyphens
    // 3. Remove any characters that don't match [a-zA-Z0-9_-]
    // 4. Replace multiple consecutive hyphens with a single hyphen
    // 5. Remove leading/trailing hyphens
    const generatedSlug = trimmedTitle
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    // If generated slug is empty (e.g., title was only special characters),
    // set a default slug based on the title length or use a fallback
    const finalSlug = generatedSlug || 'page';

    const aixcError = validateSitePath(path, finalSlug);

    setState(x => ({
      ...x,
      slug: finalSlug,
      slugError: '',
      aixcError
    }));
  }

  // A create link can name the category (?category=/us/events): choose it once the tree has loaded,
  // the same way clicking it would, so permission and site-path checks still apply.
  // A name that matches no category says so, instead of leaving the generic "choose a category" hint.
  const preselected = useRef(false);
  const [unknownCategory, setUnknownCategory] = useState('');
  useEffect(() => {
    if (preselected.current || !sitemap || !props.initialCategoryPath || props.path) return;
    const found = findCategoryByPath(sitemap.rootCategories, props.initialCategoryPath);
    // The tree may come from the cache first; wait for the server's answer before giving up.
    if (!found && isFetching) return;
    preselected.current = true;
    if (found) handlePathChange(found.path, found._id, found.canUserEdit);
    else setUnknownCategory(props.initialCategoryPath);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitemap, isFetching]);

  // Check if generate slug button should be enabled
  const canGenerateSlug = title.trim() && !categoryError && category;

  const handleOnCreate = () => {
    const { onCreate } = props;

    // Category error and AIXC error should prevent creating page. Slug error is just for info

    if (!categoryError && !aixcError) {
      // Remove any whitespace and forward slash ('/') in the slug.
      const newSlug = slug.slice();
      const filteredSlug = newSlug.replace(/[\s/]/g, '');

      // Validate again after filtering slug
      const finalAixcError = validateSitePath(path, filteredSlug);
      if (finalAixcError) {
        setState(x => ({
          ...x,
          aixcError: finalAixcError
        }));
        return;
      }

      // Save it back to state to reflect the change.
      setState(x => ({
        ...x,
        slug: filteredSlug
      }));
      onCreate(title, path,
        filteredSlug, category);
    }
  }

  // Refreshing a cached tree keeps the form (and what's typed in it) on screen.
  if (isFetching && !sitemap) return <span>Loading...</span>;

  const templateLabel = templateName || (templateId && Templates[templateId]?.schema?.name) || 'N/A';

  const categories = sitemap ? getSiteCategories(sitemap.rootCategories) : [];

  // PICK_CATEGORY is only a marker for "nothing chosen yet"; next to another error it reads as the usual message.
  const categoryMessage = categoryError === PICK_CATEGORY ? _('ERROR_CREATE_PAGE_CATEGORY') : categoryError;
  const warning = categoryError === PICK_CATEGORY && !aixcError && !slugError && !copyError ? (
    <div className={c.hintMsg} data-testid="category-hint">
      <i className="material-icons" aria-hidden>arrow_back</i>
      {unknownCategory ? `There’s no category “${unknownCategory}”. ` : ''}Choose the page’s category: click a name (like “US”) to select it.
    </div>
  ) : (categoryError || slugError || copyError || aixcError) && (
    <div className={c.warningMsg}>
      <i className="material-icons">report</i>
      {categoryMessage || aixcError || slugError || copyError}
    </div>
  );

  const additionalStyles = {
    width: '500px'
  };

  return (
    <div>
      <div className={c.formRow}>
        <div className={c.fieldLabel}>
          Title
        </div>
        <TextInput
          type="text"
          ariaLabel="Title"
          placeholder="Page Title"
          value={title}
          onChange={e => handleTitleChange(e.target.value)}
          disabled={disabled}
          additionalStyles={additionalStyles}
        />
      </div>
      <div className={c.formRow}>
        <div className={c.fieldLabel}>
          Category
        </div>
        <div className={c.formContent}>
          <ChainSelector
            rootCategories={categories}
            path={path}
            onPathChange={handlePathChange}
            disabled={disabled}
          />
          {warning}
        </div>
      </div>
      <div className={c.formRow}>
        <div className={c.fieldLabel}>
          Path
        </div>
        <div className={c.slugContainer}>
          <span className={c.pagePath}>
            {`${transformPathDisplay(path)}/`}
          </span>
          <div className={c.slugInput}>
            <TextInput
              type="text"
              ariaLabel="Path (slug)"
              placeholder="slug"
              value={slug}
              onChange={e => handleSlugChange(e.target.value)}
              disabled={disabled}
            />
          </div>
          <Button
            size="sm"
            icon="auto_fix_high"
            className={c.generateSlugBtn}
            onClick={handleGenerateSlug}
            disabled={disabled || !canGenerateSlug}
            title={!canGenerateSlug ? 'Enter a title and select a category first' : 'Generate slug from title'}
          >
            Generate path
          </Button>
        </div>
      </div>
      <div className={c.formRow}>
        <div className={c.fieldLabel}>
          {editorType === 'studio' ? 'Editor' : 'Template'}
        </div>
        <TextInput
          type="text"
          placeholder={editorType === 'studio' ? 'Editor' : 'Template'}
          value={templateLabel}
          disabled
        />
      </div>
      <div className={c.actionGroup}>
        <Button
          variant="primary"
          onClick={handleOnCreate}
          busy={copyLoading}
          disabled={disabled || !!categoryError || !!aixcError}
          title={categoryError === PICK_CATEGORY ? 'Choose a category first' : undefined}
        >
          {copyLoading ? loadingLabel : 'Create'}
        </Button>
        {disabled || copyLoading
          ? <Button disabled>Cancel</Button>
          : <Button to="/admin">Cancel</Button>}
      </div>
    </div>
  );
}

const styles = {
  formRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    width: '80%'
  },
  fieldLabel: {
    width: '10%',
    fontSize: '12px',
    color: 'hsl(0, 0%, 30%)',
    [DARK]: { color: S.muted }
  },
  slugContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingLeft: '10px',
    flexWrap: 'wrap',
    gap: '10px'
  },
  slugInput: {
    flex: '0 1 auto',
    minWidth: '400px',
    marginLeft: '5px',
    marginTop: '14px',
  },
  generateSlugBtn: {
    flexShrink: 0,
    marginBottom: '6px'
  },
  pagePath: {
    fontSize: '13px',
    color: 'hsl(0, 0%, 20%)',
    fontWeight: '400',
    display: 'flex',
    alignItems: 'center',
    [DARK]: { color: S.fg }
  },
  actionGroup: {
    display: 'flex',
    gap: 8,
    marginTop: '20px'
  },
  hintMsg: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    alignSelf: 'center',
    marginLeft: '12px',
    fontSize: '13px',
    color: 'hsl(220, 4%, 42%)',
    '& > .material-icons': {
      fontSize: '16px'
    },
    [DARK]: { color: S.muted }
  },
  warningMsg: {
    color: 'red',
    [DARK]: { color: S.red },
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-end',
    fontSize: '12px',
    '> .material-icons': {
      fontSize: '10px',
      marginRight: '10px'
    }
  },
  formContent: {
    display: 'flex'
  }
};


/* =============================================================================
=    GraphQL: Get categories/template
============================================================================= */
const QUERY = gql`
  query getData {
    sitemap {
      rootCategories
    }
  }
`;

export default injectSheet(styles)(PageSettings);
